/**
 * Per-field sentinel sets for the reference audit (Codex#1 F8 / Codex#2 P1-2).
 *
 * Package-private to the `referenceAudit*` module family; re-exported through
 * `referenceAudit.ts` for the orchestrator's needs only — NOT part of the
 * §5.2 public API.
 *
 * NON-EMPTY special tokens ONLY. The empty string "" is "unset" and is
 * handled by the required-flag branch in `checkName`, NEVER by a sentinel.
 * A sentinel set must therefore never contain "".
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §4 / §7.
 */

import { ARROWHEAD_NAME_SENTINELS } from "./builtinArrowNames";

/** No non-empty sentinel — plain symbol-table name ref. */
export const SENT_NONE: ReadonlySet<string> = new Set<string>();

/**
 * Linetype sentinels (Codex#1 F8) — `ByLayer` / `ByBlock` resolve a linetype
 * from the owning layer / block at render time and are NOT linetype-table
 * names; `Continuous` is the implicit solid line. These bypass the linetype
 * refcheck ONLY for linetype fields (entity.linetype, layer.linetype,
 * mleaderStyle.leaderLineType), never for layer/style/block-name fields.
 * Case-insensitive (stored lowercase). Enabled after PR-LT2 (plan §10.5).
 */
export const SENT_LINETYPE: ReadonlySet<string> = new Set<string>([
  "bylayer",
  "byblock",
  "continuous",
]);

/**
 * Arrowhead block sentinels — AutoCAD's built-in arrowheads live in code,
 * NOT in the BLOCKS table, so DIMBLK / arrowheadBlock refs to them must not
 * false-positive. From ezdxf `ARROWS`; "." / "" = default closed-filled.
 * Case-insensitive (stored lowercase).
 *
 * Codex SL-11 round-3 #2 — DERIVED from the SINGLE SOURCE OF TRUTH the export
 * stamp resolves against ({@link ARROWHEAD_NAME_SENTINELS} ← `BUILTIN_ACAD_ARROW_NAMES`),
 * covering BOTH the unprefixed (`open90`) and canonical `_<NAME>` (`_open90`)
 * spellings, so the audit and the exporter can no longer disagree (the old
 * hand-listed set whitelisted only `_open90` while the exporter also accepted the
 * unprefixed `OPEN90`, false-flagging it; it also carried non-ezdxf aliases
 * `_rightangle`/`_box`/`_datum` the exporter never resolves).
 */
export const SENT_ARROWHEAD: ReadonlySet<string> = ARROWHEAD_NAME_SENTINELS;
